import { useState, useEffect, useCallback } from "react";
import { Download, TrendingUp, Loader2, LayoutGrid } from "lucide-react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { formatPYGCompact } from "../utils/format";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";

// Auditoría 2026-08-27, CAMBIO 2 — Consolidado anual. El endpoint
// /api/finance/consolidado?year=YYYY ya existía en el backend y no lo
// consumía ningún archivo del frontend; esta es su pantalla.
const CURRENT_YEAR = new Date().getFullYear();

export default function FinanzasConsolidado() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async (y) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/finance/consolidado?year=${y}`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar el consolidado anual");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(year); }, [year, fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/finance/consolidado/export-excel?year=${year}`, {
        withCredentials: true, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Consolidado_${year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Error al exportar el consolidado a Excel");
    } finally { setExporting(false); }
  };

  const years = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

  return (
    <div className="space-y-5" data-testid="finanzas-consolidado-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <LayoutGrid size={20} className="text-[#2E6DA4]" /> Consolidado Anual
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Previsto, utilizado, certificado y cobrado (neto) por obra</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            data-testid="consolidado-year-select"
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2E6DA4] bg-white"
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button
            onClick={handleExport}
            disabled={exporting || loading}
            data-testid="consolidado-export-excel-btn"
            className="flex items-center gap-2 px-4 py-2 bg-[#2E6DA4] text-white text-sm font-semibold rounded-lg hover:bg-[#265a8a] disabled:opacity-50 transition-all"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Exportar Excel
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}</div>
      ) : !data || data.obras.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200/60 p-10 text-center text-gray-400" data-testid="consolidado-empty-state">
          <TrendingUp size={32} className="mx-auto mb-2 opacity-40" />
          Sin obras registradas en {year}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="consolidado-table">
              <thead>
                <tr className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="text-left px-4 py-3">Obra</th>
                  <th className="text-right px-4 py-3">Previsto Mat.</th>
                  <th className="text-right px-4 py-3">Previsto M.O.</th>
                  <th className="text-right px-4 py-3">Previsto G.A.</th>
                  <th className="text-right px-4 py-3">Utilizado Mat.</th>
                  <th className="text-right px-4 py-3">Utilizado M.O.</th>
                  <th className="text-right px-4 py-3">Utilizado G.A.</th>
                  <th className="text-right px-4 py-3">Certificado (neto)</th>
                  <th className="text-right px-4 py-3">Cobrado (neto)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.obras.map((o) => (
                  <tr key={o.obra_id} className="hover:bg-gray-50/60 transition-colors" data-testid={`consolidado-row-${o.obra_id}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{o.obra_nombre}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{formatPYGCompact(o.previsto.materiales)}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{formatPYGCompact(o.previsto.mano_obra)}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{formatPYGCompact(o.previsto.gastos_admin)}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{formatPYGCompact(o.utilizado.materiales)}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{formatPYGCompact(o.utilizado.mano_obra)}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{formatPYGCompact(o.utilizado.gastos_admin)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-[#2E6DA4]">{formatPYGCompact(o.certificado)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-emerald-600">{formatPYGCompact(o.cobrado)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-blue-50/60 font-bold text-gray-900" data-testid="consolidado-totales-row">
                  <td className="px-4 py-3">TOTALES</td>
                  <td className="px-4 py-3 text-right">{formatPYGCompact(data.total_previsto.materiales)}</td>
                  <td className="px-4 py-3 text-right">{formatPYGCompact(data.total_previsto.mano_obra)}</td>
                  <td className="px-4 py-3 text-right">{formatPYGCompact(data.total_previsto.gastos_admin)}</td>
                  <td className="px-4 py-3 text-right">{formatPYGCompact(data.total_utilizado.materiales)}</td>
                  <td className="px-4 py-3 text-right">{formatPYGCompact(data.total_utilizado.mano_obra)}</td>
                  <td className="px-4 py-3 text-right">{formatPYGCompact(data.total_utilizado.gastos_admin)}</td>
                  <td className="px-4 py-3 text-right text-[#2E6DA4]">{formatPYGCompact(data.total_certificado)}</td>
                  <td className="px-4 py-3 text-right text-emerald-600">{formatPYGCompact(data.total_cobrado)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
